import { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import FontAwesomeIcon from '../components/FontAwesomeIcon'
import FAQSection from '../components/FAQSection'

export default function PlanYourTrip() {
  const [currentStep, setCurrentStep] = useState(1)
  const [formData, setFormData] = useState({
    // Step 1: Trip Type
    tripTypes: [],

    // Step 2: Destinations
    destinations: [],
    notSureDestinations: false,

    // Step 3: Travel Details
    travelers: 2,
    travelMonth: '',
    tripDuration: '',
    budgetRange: '',

    // Step 4: Accommodation
    accommodationPreferences: [],

    // Step 5: Special Interests
    specialInterests: [],

    // Step 6: Contact Info
    fullName: '',
    email: '',
    phone: '',
    country: '',
    additionalComments: ''
  })

  const [errors, setErrors] = useState({})
  const [isSubmitted, setIsSubmitted] = useState(false)

  const totalSteps = 6

  // Scroll to top when step changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [currentStep])

  const scrollContainerRef = useRef(null)

  const scroll = (direction) => {
    const container = scrollContainerRef.current
    if (container) {
      const scrollAmount = direction === 'left' ? -400 : 400
      container.scrollBy({ left: scrollAmount, behavior: 'smooth' })
    }
  }

  const tripTypeOptions = [
    {
      value: 'wildlife-safari',
      label: 'Safari & Wildlife',
      icon: 'fa-paw',
      description: 'Big Five game drives and wildlife encounters'
    },
    {
      value: 'gorilla-trekking',
      label: 'Gorilla Trekking',
      icon: 'fa-mountain',
      description: 'Encounter mountain gorillas in their natural habitat'
    },
    {
      value: 'mountain-adventures',
      label: 'Mountain Adventures',
      icon: 'fa-mountain',
      description: 'Hiking, trekking, and mountain expeditions'
    },
    {
      value: 'cultural-experiences',
      label: 'Cultural Experiences',
      icon: 'fa-users',
      description: 'Immerse yourself in local communities and traditions'
    },
    {
      value: 'combination',
      label: 'Combination Trips',
      icon: 'fa-compass',
      description: 'Mix of wildlife, culture, and adventure'
    }
  ]

  const destinationOptions = [
    { value: 'bwindi', label: 'Bwindi Impenetrable Forest', category: 'Primates' },
    { value: 'queen-elizabeth', label: 'Queen Elizabeth National Park', category: 'Wildlife' },
    { value: 'murchison-falls', label: 'Murchison Falls', category: 'Wildlife' },
    { value: 'kibale', label: 'Kibale National Park', category: 'Primates' },
    { value: 'rwenzori', label: 'Rwenzori Mountains', category: 'Mountains' },
    { value: 'lake-mburo', label: 'Lake Mburo', category: 'Wildlife' },
    { value: 'mount-elgon', label: 'Mount Elgon', category: 'Mountains' },
    { value: 'semuliki', label: 'Semuliki Valley', category: 'Birding' }
  ]

  const accommodationOptions = [
    { value: 'luxury-lodges', label: 'Luxury Lodges', icon: 'fa-crown', description: '5-star safari lodges with premium amenities' },
    { value: 'eco-lodges', label: 'Eco-Lodges', icon: 'fa-leaf', description: 'Sustainable, environmentally friendly accommodations' },
    { value: 'tented-camps', label: 'Tented Camps', icon: 'fa-campground', description: 'Luxury camping with authentic safari experience' },
    { value: 'community-homestays', label: 'Community Homestays', icon: 'fa-home', description: 'Stay with local families for cultural immersion' },
    { value: 'budget-camping', label: 'Budget Camping', icon: 'fa-tent', description: 'Affordable camping options' },
    { value: 'mix', label: 'Mix of Options', icon: 'fa-layer-group', description: 'Variety of accommodation types' }
  ]

  const specialInterestOptions = [
    { value: 'photography', label: 'Photography Focus', icon: 'fa-camera' },
    { value: 'bird-watching', label: 'Bird Watching', icon: 'fa-dove' },
    { value: 'cultural-immersion', label: 'Cultural Immersion', icon: 'fa-users' },
    { value: 'adventure-activities', label: 'Adventure Activities', icon: 'fa-person-hiking' },
    { value: 'green-paths', label: 'Planting Green Paths', icon: 'fa-seedling' },
    { value: 'family-friendly', label: 'Family-Friendly Activities', icon: 'fa-children' }
  ]

  const popularPackages = [
    {
      slug: '12-days-wildlife-safari',
      title: '12 Days Uganda Wildlife Safari Tour',
      duration: '12 Days',
      description: 'Perfect balance of primates and wildlife across five magnificent national parks',
      price: 'From $3,800pp',
      image: '/images/packages/12-days-safari.jpg',
      highlights: ['Mountain gorillas in Bwindi', 'Chimpanzees in Kibale', 'Big Five wildlife', 'Kazinga Channel cruise'],
      route: '/packages/12-days-wildlife-safari'
    },
    {
      slug: '8-days-highlights-safari',
      title: '8 Days Uganda Safari Highlights',
      duration: '8 Days',
      description: 'Uganda\'s greatest hits in one perfect week-plus adventure',
      price: 'From $2,806pp',
      image: '/images/packages/8-days-highlights.jpg',
      highlights: ['Gorilla trekking', 'Chimp tracking', 'Murchison Falls', 'Tree-climbing lions'],
      route: '/packages/8-days-highlights-safari'
    },
    {
      slug: '7-days-gorilla-chimp-safari',
      title: '7 Days Gorilla & Chimpanzee Safari',
      duration: '7 Days',
      description: 'Ultimate primate experience combining gorillas and chimps',
      price: 'Contact Us',
      image: '/images/packages/7-days-gorilla-chimp.jpg',
      highlights: ['Mountain gorillas', 'Chimpanzee tracking', 'Queen Elizabeth NP', 'Primate focus'],
      route: '/packages/7-days-gorilla-chimp-safari'
    },
    {
      slug: '5-days-gorillas-lions',
      title: '5 Day Gorillas and Lions Tour',
      duration: '5 Days',
      description: 'Maximum impact safari for limited time travelers',
      price: 'From $2,100pp',
      image: '/images/packages/5-days-gorillas-lions.jpg',
      highlights: ['Mountain gorillas', 'Tree-climbing lions', 'Ishasha sector', 'Quick adventure'],
      route: '/packages/5-days-gorillas-lions'
    },
    {
      slug: 'bwindi-gorilla-trekking',
      title: '3 Days Bwindi Gorilla Trekking',
      duration: '3 Days',
      description: 'Encounter mountain gorillas in ancient rainforest',
      price: '$750pp',
      image: '/images/packages/3-days-bwindi.jpg',
      highlights: ['Gorilla permit included', 'Expert guides', 'Forest accommodation', 'Ancient rainforest'],
      route: '/experiences/bwindi-gorilla-trekking'
    },
    {
      slug: 'queen-elizabeth-safari',
      title: '3 Days Queen Elizabeth Safari',
      duration: '3 Days',
      description: 'Tree-climbing lions and Kazinga Channel boat safari',
      price: '$450pp',
      image: '/images/packages/3-days-queen-elizabeth.jpg',
      highlights: ['Tree-climbing lions', 'Kazinga Channel', 'Game drives', 'Boat safari'],
      route: '/experiences/queen-elizabeth-safari'
    },
    {
      slug: 'murchison-falls-safari',
      title: '4 Days Murchison Falls Safari',
      duration: '4 Days',
      description: 'The mighty Nile\'s explosive power and Big Five wildlife',
      price: '$650pp',
      image: '/images/packages/4-days-murchison-chimp.jpg',
      highlights: ['Murchison Falls', 'Nile boat cruise', 'Big Five', 'Largest park'],
      route: '/experiences/murchison-falls-safari'
    },
    {
      slug: 'kibale-chimp-tracking',
      title: '3 Days Kibale Chimp Tracking',
      duration: '3 Days',
      description: 'Primate capital of the world - chimpanzees and 12 other species',
      price: '$200pp',
      image: '/images/chimpanzee-kibale.jpg',
      highlights: ['Chimpanzee tracking', '13 primate species', 'Bigodi wetland', 'Bird watching'],
      route: '/experiences/kibale-chimp-tracking'
    }
  ]

  const testimonials = [
    {
      name: 'Sarah Johnson',
      country: 'USA',
      rating: 5,
      trip: 'Gorilla Trekking',
      comment: 'The planning process was seamless! The team understood exactly what we wanted and created the perfect itinerary. Seeing the gorillas was life-changing.',
      image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=150'
    },
    {
      name: 'Michael Chen',
      country: 'Singapore',
      rating: 5,
      trip: 'Ultimate Uganda Safari',
      comment: 'From the first inquiry to the final day, everything was perfectly organized. The custom itinerary they created exceeded all our expectations.',
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150'
    },
    {
      name: 'Emma Williams',
      country: 'UK',
      rating: 5,
      trip: 'Cultural & Wildlife Combo',
      comment: 'The planning team took time to understand our interests and budget. The result was an incredible journey that balanced adventure with cultural immersion.',
      image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150'
    }
  ]

  const faqItems = [
    {
      question: 'How far in advance should I book?',
      answer: 'We recommend booking 3-6 months in advance, especially for gorilla trekking (permits required) and peak seasons (June-September, December-February). However, we can often accommodate last-minute bookings depending on availability.'
    },
    {
      question: 'What\'s included in the price?',
      answer: 'Our packages typically include accommodation, meals, park entrance fees, activities, professional guide, and ground transportation. International flights, visa fees, travel insurance, and personal expenses are usually not included. We provide a detailed breakdown for each custom itinerary.'
    },
    {
      question: 'Can I customize a package?',
      answer: 'Absolutely! All our trips are fully customizable. Whether you want to add extra days, change accommodations, include specific activities, or create a completely unique itinerary, we\'ll work with you to design your perfect safari.'
    },
    {
      question: 'What about gorilla and chimp permits?',
      answer: 'Gorilla permits in Uganda cost $700 per person and chimp permits cost $250. We handle all permit bookings on your behalf and include them in your package. These permits should be secured well in advance as they\'re limited and in high demand.'
    },
    {
      question: 'Do I need travel insurance?',
      answer: 'Yes, we strongly recommend comprehensive travel insurance that covers medical emergencies, trip cancellations, and emergency evacuation. This is especially important for activities like gorilla trekking and mountain hiking.'
    },
    {
      question: 'What are the payment terms?',
      answer: 'We typically require a 30% deposit to confirm your booking and secure permits/accommodations. The balance is due 30 days before your trip starts. We accept bank transfers and major credit cards. Payment schedules can be discussed based on your booking timeline.'
    }
  ]



  const handleMultiSelect = (value, fieldName) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: prev[fieldName].includes(value)
        ? prev[fieldName].filter(item => item !== value)
        : [...prev[fieldName], value]
    }))
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }))
    }
  }

  const validateStep = (step) => {
    const newErrors = {}

    switch (step) {
      case 1:
        if (formData.tripTypes.length === 0) {
          newErrors.tripTypes = 'Please select at least one trip type'
        }
        break
      case 2:
        if (formData.destinations.length === 0 && !formData.notSureDestinations) {
          newErrors.destinations = 'Please select at least one destination or check "Not sure yet"'
        }
        break
      case 3:
        if (!formData.tripDuration) newErrors.tripDuration = 'Please select trip duration'
        if (!formData.budgetRange) newErrors.budgetRange = 'Please select budget range'
        break
      case 4:
        if (formData.accommodationPreferences.length === 0) {
          newErrors.accommodationPreferences = 'Please select at least one accommodation preference'
        }
        break
      case 6:
        if (!formData.fullName.trim()) newErrors.fullName = 'Full name is required'
        if (!formData.email.trim()) newErrors.email = 'Email is required'
        else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email is invalid'
        if (!formData.phone.trim()) newErrors.phone = 'Phone number is required'
        if (!formData.country.trim()) newErrors.country = 'Country is required'
        break
      default:
        break
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, totalSteps))
    }
  }

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (validateStep(currentStep)) {
      try {
        // Prepare form data for Netlify
        const netlifyFormData = new URLSearchParams({
          'form-name': 'safari-planning',
          'bot-field': '',
          'fullName': formData.fullName,
          'email': formData.email,
          'phone': formData.phone,
          'country': formData.country,
          'tripTypes': formData.tripTypes.join(', '),
          'destinations': formData.destinations.join(', ') || (formData.notSureDestinations ? 'Need Recommendations' : ''),
          'travelers': formData.travelers.toString(),
          'travelMonth': formData.travelMonth || 'Flexible',
          'tripDuration': formData.tripDuration,
          'budgetRange': formData.budgetRange,
          'accommodationPreferences': formData.accommodationPreferences.join(', '),
          'specialInterests': formData.specialInterests.join(', ') || 'None',
          'additionalComments': formData.additionalComments || 'None'
        }).toString()

        // Submit to Netlify Forms
        const response = await fetch('/', {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: netlifyFormData
        })

        if (response.ok) {
          console.log('Trip Plan Submitted:', formData)
          setIsSubmitted(true)
          window.scrollTo({ top: 0, behavior: 'smooth' })
        } else {
          throw new Error('Form submission failed')
        }
      } catch (error) {
        console.error('Error submitting form:', error)
        alert('There was an error submitting your request. Please try again or contact us directly.')
      }
    }
  }

  const useTemplate = (packageIndex) => {
    const templates = [
      { tripTypes: ['gorilla-trekking'], destinations: ['bwindi'], tripDuration: '3-5', budgetRange: 'mid-range' },
      { tripTypes: ['wildlife-safari'], destinations: ['queen-elizabeth', 'murchison-falls'], tripDuration: '3-5', budgetRange: 'mid-range' },
      { tripTypes: ['combination'], destinations: ['bwindi', 'queen-elizabeth', 'kibale'], tripDuration: '9-12', budgetRange: 'luxury' },
      { tripTypes: ['mountain-adventures', 'wildlife-safari'], destinations: ['rwenzori', 'queen-elizabeth'], tripDuration: '6-8', budgetRange: 'mid-range' }
    ]

    if (templates[packageIndex]) {
      setFormData(prev => ({ ...prev, ...templates[packageIndex] }))
      setCurrentStep(1)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  const ProgressIndicator = () => (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-4">
        {[1, 2, 3, 4, 5, 6].map((step) => (
          <div key={step} className="flex items-center flex-1">
            <div className={`flex items-center justify-center w-10 h-10 rounded-full font-bold ${step === currentStep
              ? 'bg-safari-gold text-white'
              : step < currentStep
                ? 'bg-safari-green text-white'
                : 'bg-gray-200 text-gray-500'
              }`}>
              {step < currentStep ? '✓' : step}
            </div>
            {step < 6 && (
              <div className={`flex-1 h-1 mx-2 ${step < currentStep ? 'bg-safari-green' : 'bg-gray-200'
                }`}></div>
            )}
          </div>
        ))}
      </div>
      <div className="text-center">
        <span className="text-sm text-gray-600">Step {currentStep} of {totalSteps}</span>
      </div>
    </div>
  )

  const StepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div>
            <h2 className="text-3xl font-bold mb-4 text-safari-green text-center">What type of safari experience are you looking for?</h2>
            <p className="text-gray-600 text-center mb-8">Select all that interest you</p>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {tripTypeOptions.map(option => (
                <div
                  key={option.value}
                  onClick={() => handleMultiSelect(option.value, 'tripTypes')}
                  className={`cursor-pointer p-6 border-2 rounded-xl transition-all duration-300 hover:shadow-lg ${formData.tripTypes.includes(option.value)
                    ? 'border-safari-green bg-safari-green bg-opacity-5 shadow-md'
                    : 'border-gray-200 hover:border-safari-green'
                    }`}
                >
                  <div className="text-center">
                    <FontAwesomeIcon icon={option.icon} className="w-12 h-12 mx-auto mb-4 text-safari-green" />
                    <h3 className="font-bold text-lg mb-2">{option.label}</h3>
                    <p className="text-sm text-gray-600">{option.description}</p>
                    {formData.tripTypes.includes(option.value) && (
                      <div className="mt-3">
                        <span className="inline-block px-3 py-1 bg-safari-green text-white text-xs rounded-full">Selected</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            {errors.tripTypes && <p className="text-red-500 text-center mt-4">{errors.tripTypes}</p>}
          </div>
        )

      case 2:
        return (
          <div>
            <h2 className="text-3xl font-bold mb-4 text-safari-green text-center">Which destinations interest you?</h2>
            <p className="text-gray-600 text-center mb-8">Select your preferred locations</p>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {destinationOptions.map(option => (
                <div
                  key={option.value}
                  onClick={() => !formData.notSureDestinations && handleMultiSelect(option.value, 'destinations')}
                  className={`cursor-pointer p-4 border-2 rounded-lg transition-all ${formData.destinations.includes(option.value)
                    ? 'border-safari-green bg-safari-green bg-opacity-5'
                    : formData.notSureDestinations
                      ? 'border-gray-200 opacity-50 cursor-not-allowed'
                      : 'border-gray-200 hover:border-safari-green'
                    }`}
                >
                  <h3 className="font-semibold text-sm mb-1">{option.label}</h3>
                  <span className="text-xs text-gray-500">{option.category}</span>
                  {formData.destinations.includes(option.value) && (
                    <div className="mt-2">
                      <span className="inline-block px-2 py-0.5 bg-safari-green text-white text-xs rounded">✓</span>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="text-center">
              <label className="inline-flex items-center cursor-pointer p-4 border-2 border-dashed rounded-lg hover:bg-gray-50">
                <input
                  type="checkbox"
                  checked={formData.notSureDestinations}
                  onChange={(e) => {
                    handleChange(e)
                    if (e.target.checked) {
                      setFormData(prev => ({ ...prev, destinations: [] }))
                    }
                  }}
                  name="notSureDestinations"
                  className="h-5 w-5 text-safari-green focus:ring-safari-gold mr-3"
                />
                <span className="font-medium">Not sure yet - I need recommendations</span>
              </label>
            </div>
            {errors.destinations && <p className="text-red-500 text-center mt-4">{errors.destinations}</p>}
          </div>
        )

      case 3:
        return (
          <div>
            <h2 className="text-3xl font-bold mb-4 text-safari-green text-center">Safari Travel Details</h2>
            <p className="text-gray-600 text-center mb-8">Help us design your perfect Uganda safari adventure</p>

            <div className="max-w-2xl mx-auto space-y-6">
              <div>
                <label className="block text-sm font-semibold mb-3">Number of Travelers</label>
                <div className="flex items-center justify-center space-x-4">
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, travelers: Math.max(1, prev.travelers - 1) }))}
                    className="w-12 h-12 rounded-full bg-safari-green text-white text-2xl hover:bg-opacity-80"
                  >
                    -
                  </button>
                  <span className="text-3xl font-bold w-20 text-center">{formData.travelers}</span>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, travelers: Math.min(20, prev.travelers + 1) }))}
                    className="w-12 h-12 rounded-full bg-safari-green text-white text-2xl hover:bg-opacity-80"
                  >
                    +
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-3">Preferred Travel Month</label>
                <select
                  name="travelMonth"
                  value={formData.travelMonth}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-safari-gold"
                >
                  <option value="">Select a month (optional)</option>
                  <option value="january">January</option>
                  <option value="february">February</option>
                  <option value="march">March</option>
                  <option value="april">April</option>
                  <option value="may">May</option>
                  <option value="june">June</option>
                  <option value="july">July</option>
                  <option value="august">August</option>
                  <option value="september">September</option>
                  <option value="october">October</option>
                  <option value="november">November</option>
                  <option value="december">December</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-3">Safari Duration <span className="text-red-500">*</span></label>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { value: '3-5', label: '3-5 days' },
                    { value: '6-8', label: '6-8 days' },
                    { value: '9-12', label: '9-12 days' },
                    { value: '13+', label: '13+ days' }
                  ].map(option => (
                    <label
                      key={option.value}
                      className={`cursor-pointer p-4 border-2 rounded-lg text-center transition ${formData.tripDuration === option.value
                        ? 'border-safari-green bg-safari-green bg-opacity-5 font-semibold'
                        : 'border-gray-200 hover:border-safari-green'
                        }`}
                    >
                      <input
                        type="radio"
                        name="tripDuration"
                        value={option.value}
                        checked={formData.tripDuration === option.value}
                        onChange={handleChange}
                        className="sr-only"
                      />
                      {option.label}
                    </label>
                  ))}
                </div>
                {errors.tripDuration && <p className="text-red-500 text-sm mt-2">{errors.tripDuration}</p>}
              </div>

              <div>
                <label className="block text-sm font-semibold mb-3">Budget Range (per person) <span className="text-red-500">*</span></label>
                <div className="space-y-3">
                  {[
                    { value: 'budget', label: 'Budget', range: '$100-200/day', desc: 'Basic lodges & camping' },
                    { value: 'mid-range', label: 'Mid-Range', range: '$200-400/day', desc: 'Comfortable lodges & good amenities' },
                    { value: 'luxury', label: 'Luxury', range: '$400-700/day', desc: 'Premium lodges & exclusive experiences' },
                    { value: 'ultra-luxury', label: 'Ultra-Luxury', range: '$700+/day', desc: 'Top-tier lodges & private experiences' }
                  ].map(option => (
                    <label
                      key={option.value}
                      className={`cursor-pointer flex items-center p-4 border-2 rounded-lg transition ${formData.budgetRange === option.value
                        ? 'border-safari-green bg-safari-green bg-opacity-5'
                        : 'border-gray-200 hover:border-safari-green'
                        }`}
                    >
                      <input
                        type="radio"
                        name="budgetRange"
                        value={option.value}
                        checked={formData.budgetRange === option.value}
                        onChange={handleChange}
                        className="h-5 w-5 text-safari-green focus:ring-safari-gold"
                      />
                      <div className="ml-4">
                        <div className="font-semibold">{option.label} - {option.range}</div>
                        <div className="text-sm text-gray-600">{option.desc}</div>
                      </div>
                    </label>
                  ))}
                </div>
                {errors.budgetRange && <p className="text-red-500 text-sm mt-2">{errors.budgetRange}</p>}
              </div>
            </div>
          </div>
        )

      case 4:
        return (
          <div>
            <h2 className="text-3xl font-bold mb-4 text-safari-green text-center">Accommodation Preferences</h2>
            <p className="text-gray-600 text-center mb-8">Choose your preferred accommodation style(s)</p>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {accommodationOptions.map(option => (
                <div
                  key={option.value}
                  onClick={() => handleMultiSelect(option.value, 'accommodationPreferences')}
                  className={`cursor-pointer p-6 border-2 rounded-xl transition-all hover:shadow-lg ${formData.accommodationPreferences.includes(option.value)
                    ? 'border-safari-green bg-safari-green bg-opacity-5 shadow-md'
                    : 'border-gray-200 hover:border-safari-green'
                    }`}
                >
                  <div className="text-center">
                    <FontAwesomeIcon icon={option.icon} className="w-10 h-10 mx-auto mb-3 text-safari-green" />
                    <h3 className="font-bold mb-2">{option.label}</h3>
                    <p className="text-sm text-gray-600">{option.description}</p>
                    {formData.accommodationPreferences.includes(option.value) && (
                      <div className="mt-3">
                        <span className="inline-block px-3 py-1 bg-safari-green text-white text-xs rounded-full">Selected</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            {errors.accommodationPreferences && <p className="text-red-500 text-center mt-4">{errors.accommodationPreferences}</p>}
          </div>
        )

      case 5:
        return (
          <div>
            <h2 className="text-3xl font-bold mb-4 text-safari-green text-center">Special Interests & Add-ons</h2>
            <p className="text-gray-600 text-center mb-8">Optional: Select any special interests (or skip)</p>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto">
              {specialInterestOptions.map(option => (
                <label
                  key={option.value}
                  className={`cursor-pointer flex items-center p-4 border-2 rounded-lg transition ${formData.specialInterests.includes(option.value)
                    ? 'border-safari-green bg-safari-green bg-opacity-5'
                    : 'border-gray-200 hover:border-safari-green'
                    }`}
                >
                  <input
                    type="checkbox"
                    checked={formData.specialInterests.includes(option.value)}
                    onChange={() => handleMultiSelect(option.value, 'specialInterests')}
                    className="h-5 w-5 text-safari-green focus:ring-safari-gold mr-3"
                  />
                  <FontAwesomeIcon icon={option.icon} className="w-5 h-5 text-safari-green mr-2" />
                  <span className="font-medium">{option.label}</span>
                </label>
              ))}
            </div>
          </div>
        )

      case 6:
        return (
          <div>
            <h2 className="text-3xl font-bold mb-4 text-safari-green text-center">Your Contact Information</h2>
            <p className="text-gray-600 text-center mb-8">Almost done! Let's finalize your safari planning request</p>

            <div className="max-w-2xl mx-auto space-y-6">
              <div>
                <label className="block text-sm font-semibold mb-2">Full Name <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-safari-gold ${errors.fullName ? 'border-red-500' : 'border-gray-300'
                    }`}
                  placeholder="John Doe"
                />
                {errors.fullName && <p className="text-red-500 text-sm mt-1">{errors.fullName}</p>}
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">Email Address <span className="text-red-500">*</span></label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-safari-gold ${errors.email ? 'border-red-500' : 'border-gray-300'
                    }`}
                  placeholder="john@example.com"
                />
                {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">Phone Number <span className="text-red-500">*</span></label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-safari-gold ${errors.phone ? 'border-red-500' : 'border-gray-300'
                    }`}
                  placeholder="+1 234 567 8900"
                />
                {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">Country of Residence <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-safari-gold ${errors.country ? 'border-red-500' : 'border-gray-300'
                    }`}
                  placeholder="United States"
                />
                {errors.country && <p className="text-red-500 text-sm mt-1">{errors.country}</p>}
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">Additional Comments or Special Requests</label>
                <textarea
                  name="additionalComments"
                  value={formData.additionalComments}
                  onChange={handleChange}
                  rows="5"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-safari-gold"
                  placeholder="Tell us about any special occasions, dietary requirements, accessibility needs, or specific experiences you'd like to include..."
                ></textarea>
              </div>
            </div>
          </div>
        )

      default:
        return null
    }
  }

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-safari-green to-safari-green-dark flex items-center justify-center px-4">
        <div className="max-w-2xl mx-auto text-center bg-white rounded-2xl shadow-2xl p-12">
          <div className="mb-6">
            <div className="w-20 h-20 bg-safari-gold rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-4xl font-bold text-safari-green mb-4">Thank You!</h2>
            <p className="text-xl text-gray-700 mb-6">
              Your safari planning request has been submitted successfully.
            </p>
            <div className="bg-safari-green bg-opacity-5 rounded-lg p-6 mb-6">
              <p className="text-gray-700 mb-4">
                Our Uganda safari specialists will review your preferences and create a personalized conservation-focused safari itinerary tailored just for you. You'll receive a detailed proposal with safari options, conservation impact, and pricing within 24 hours.
              </p>
              <p className="text-gray-600 text-sm">
                We'll contact you at: <strong>{formData.email}</strong>
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/" className="btn-primary">Return Home</Link>
              <Link to="/destinations" className="btn-secondary">Explore Destinations</Link>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-safari-green to-safari-green-dark text-white py-20">
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://images.unsplash.com/photo-1516426122078-c23e76319801?w=1200"
            alt="Uganda Safari"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl md:text-5xl font-bold mb-4 font-heading drop-shadow-lg">
              Plan Your Perfect Uganda Safari
            </h1>
            <p className="text-xl md:text-2xl mb-8 drop-shadow-lg">
              Tell us about your dream safari and we'll create a personalized itinerary that matches your interests, budget, and travel style
            </p>
            <div className="flex flex-wrap justify-center gap-6 text-sm">
              <div className="flex items-center">
                <FontAwesomeIcon icon="fa-check-circle" className="w-5 h-5 mr-2" />
                <span>100% Customizable</span>
              </div>
              <div className="flex items-center">
                <FontAwesomeIcon icon="fa-check-circle" className="w-5 h-5 mr-2" />
                <span>Expert Local Guides</span>
              </div>
              <div className="flex items-center">
                <FontAwesomeIcon icon="fa-check-circle" className="w-5 h-5 mr-2" />
                <span>Best Price Guarantee</span>
              </div>
              <div className="flex items-center">
                <FontAwesomeIcon icon="fa-check-circle" className="w-5 h-5 mr-2" />
                <span>24/7 Support</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Plan With Us */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12 text-safari-green">Why Plan With Us?</h2>
          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center">
              <FontAwesomeIcon icon="fa-magic" className="w-16 h-16 mx-auto mb-4 text-safari-gold" />
              <h3 className="font-bold text-lg mb-2">Tailor-Made Itineraries</h3>
              <p className="text-sm text-gray-600">Every safari is customized to your preferences, interests, and budget</p>
            </div>
            <div className="text-center">
              <FontAwesomeIcon icon="fa-users" className="w-16 h-16 mx-auto mb-4 text-safari-gold" />
              <h3 className="font-bold text-lg mb-2">Local Safari Experts</h3>
              <p className="text-sm text-gray-600">Ugandan specialists with deep knowledge of wildlife, parks, and the best safari experiences</p>
            </div>
            <div className="text-center">
              <FontAwesomeIcon icon="fa-dollar-sign" className="w-16 h-16 mx-auto mb-4 text-safari-gold" />
              <h3 className="font-bold text-lg mb-2">Best Price Guarantee</h3>
              <p className="text-sm text-gray-600">Competitive pricing with no hidden fees</p>
            </div>
            <div className="text-center">
              <FontAwesomeIcon icon="fa-leaf" className="w-16 h-16 mx-auto mb-4 text-safari-gold" />
              <h3 className="font-bold text-lg mb-2">Sustainable Tourism</h3>
              <p className="text-sm text-gray-600">Support local communities and conservation efforts (optional add-on available)</p>
            </div>
          </div>
        </div>
      </section>

      {/* Multi-Step Form */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-5xl">
          <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-xl p-8 md:p-12" style={{ border: '1px solid #24684f' }}>
            <ProgressIndicator />
            <StepContent />

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-12 pt-8 border-t">
              {currentStep > 1 && (
                <button
                  type="button"
                  onClick={handleBack}
                  className="px-8 py-3 border-2 border-safari-green text-safari-green font-semibold rounded-lg hover:bg-safari-green hover:text-white transition"
                >
                  Back
                </button>
              )}
              {currentStep < totalSteps ? (
                <button
                  type="button"
                  onClick={handleNext}
                  className="btn-primary px-8 py-3 ml-auto"
                >
                  Next Step
                </button>
              ) : (
                <button
                  type="submit"
                  className="btn-primary px-8 py-3 ml-auto"
                >
                  Submit Safari Planning Request
                </button>
              )}
            </div>
          </form>
        </div>
      </section>

      {/* Popular Trip Packages */}
      <section className="py-16 bg-safari-light">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4 text-safari-green">Popular Safari Packages</h2>
            <p className="text-gray-600">Need inspiration? Use one of our proven safari packages as a starting template</p>
          </div>

          {/* Carousel Container */}
          <div className="relative max-w-7xl mx-auto">
            {/* Left Arrow */}
            <button
              onClick={() => scroll('left')}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white shadow-lg rounded-full p-3 transition-all duration-300 hover:scale-110 hidden md:block"
              aria-label="Scroll left"
            >
              <svg className="w-6 h-6 text-safari-green" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            {/* Scrollable Container */}
            <div
              ref={scrollContainerRef}
              className="flex gap-6 overflow-x-auto scrollbar-hide scroll-smooth px-2 md:px-12 py-4"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {popularPackages.map((pkg, index) => (
                <Link
                  key={index}
                  to={pkg.route}
                  className="flex-shrink-0 bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
                  style={{ width: '320px' }}
                >
                  <div className="relative h-48">
                    <img src={pkg.image} alt={pkg.title} className="w-full h-full object-cover" />
                    <div className="absolute top-4 right-4 bg-safari-gold text-white px-3 py-1 rounded-full text-sm font-semibold">
                      {pkg.duration}
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="font-bold text-lg mb-2 text-safari-green line-clamp-2">{pkg.title}</h3>
                    <p className="text-sm text-gray-600 mb-4 line-clamp-2">{pkg.description}</p>
                    <ul className="text-xs text-gray-500 mb-4 space-y-1">
                      {pkg.highlights.slice(0, 3).map((highlight, i) => (
                        <li key={i} className="flex items-center">
                          <FontAwesomeIcon icon="fa-check" className="w-3 h-3 text-safari-green mr-2 flex-shrink-0" />
                          <span className="line-clamp-1">{highlight}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                      <span className="text-safari-green font-bold">{pkg.price}</span>
                      <span className="text-sm text-safari-gold hover:text-safari-green transition">
                        View Details →
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {/* Right Arrow */}
            <button
              onClick={() => scroll('right')}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white shadow-lg rounded-full p-3 transition-all duration-300 hover:scale-110 hidden md:block"
              aria-label="Scroll right"
            >
              <svg className="w-6 h-6 text-safari-green" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          <div className="text-center mt-12">
            <Link to="/experiences" className="btn-primary text-lg">
              View All Safari Packages
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12 text-safari-green">What Our Travelers Say</h2>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-6">
                <div className="flex items-center mb-4">
                  <img src={testimonial.image} alt={testimonial.name} className="w-16 h-16 rounded-full mr-4" />
                  <div>
                    <h4 className="font-bold">{testimonial.name}</h4>
                    <p className="text-sm text-gray-500">{testimonial.country}</p>
                    <div className="flex text-safari-gold mt-1">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <FontAwesomeIcon key={i} icon="fa-star" className="w-4 h-4" />
                      ))}
                    </div>
                  </div>
                </div>
                <p className="text-gray-700 text-sm mb-3 italic">"{testimonial.comment}"</p>
                <p className="text-xs text-safari-green font-semibold">Trip: {testimonial.trip}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <FAQSection faqs={faqItems} title="Frequently Asked Questions" />

      {/* Final CTA */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="text-center bg-safari-green text-white rounded-2xl p-12">
            <h2 className="text-3xl font-bold mb-4">Or Speak With Our Travel Experts</h2>
            <p className="text-lg mb-8">Prefer to talk to someone? Our Uganda safari specialists are ready to help you plan your perfect conservation-focused safari</p>

            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div>
                <FontAwesomeIcon icon="fa-phone" className="w-8 h-8 mx-auto mb-3" />
                <p className="font-semibold">Phone</p>
                <p className="text-sm">+256 XXX XXX XXX</p>
              </div>
              <div>
                <FontAwesomeIcon icon="fa-envelope" className="w-8 h-8 mx-auto mb-3" />
                <p className="font-semibold">Email</p>
                <p className="text-sm">info@shakestravel.com</p>
              </div>
              <div>
                <FontAwesomeIcon icon="fa-comment" className="w-8 h-8 mx-auto mb-3" />
                <p className="font-semibold">WhatsApp</p>
                <p className="text-sm">+256 XXX XXX XXX</p>
              </div>
            </div>

            <a
              href="https://wa.me/256XXXXXXXXX"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-white text-safari-green px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition"
            >
              Chat on WhatsApp
            </a>
          </div>
        </div>
      </section>
    </div>
  )
}
